import React, { useState } from 'react';
import TaxAssumptionNote from './TaxAssumptionNote';
import { GrowthRateInputs, parseGrowthRates, missingPricesAsOf } from '../utils/growthRates';

// What-if scenario comparison. Sends the selected properties plus a list of
// named parameter overrides to the backend, which runs the real engine per
// scenario and returns headline totals + delta vs base.

interface Override { target: string; op: 'mul' | 'add' | 'set'; value: number; }
interface Preset { name: string; description: string; overrides: Override[]; enabled: boolean; }

interface Props {
  properties: any[];          // selected properties (with events/owner_stays)
  startDate: string;
  endDate: string;
  granularity: string;
  growthRates: GrowthRateInputs; // F15: the analysis's rates, as typed
}

const DEFAULT_PRESETS: Preset[] = [
  { name: 'Optimistic', description: 'Occupancy ×1.1, ADR ×1.08', enabled: true,
    overrides: [{ target: 'occupancy', op: 'mul', value: 1.1 }, { target: 'adr', op: 'mul', value: 1.08 }] },
  { name: 'Pessimistic', description: 'Occupancy ×0.85, ADR ×0.92', enabled: true,
    overrides: [{ target: 'occupancy', op: 'mul', value: 0.85 }, { target: 'adr', op: 'mul', value: 0.92 }] },
  { name: 'Stress', description: 'Occupancy ×0.6, ADR ×0.8', enabled: true,
    overrides: [{ target: 'occupancy', op: 'mul', value: 0.6 }, { target: 'adr', op: 'mul', value: 0.8 }] },
  { name: '-10% occupancy', description: 'Occupancy ×0.9', enabled: false,
    overrides: [{ target: 'occupancy', op: 'mul', value: 0.9 }] },
  { name: '+1% mortgage rate', description: 'Mortgage rate +1', enabled: false,
    overrides: [{ target: 'mortgage_rate_pct', op: 'add', value: 1 }] },
  { name: '+€20 ADR', description: 'ADR +20', enabled: false,
    overrides: [{ target: 'adr', op: 'add', value: 20 }] },
];

// Targets the backend override engine understands (occupancy/adr scale the
// monthly figures; the rest are direct property fields).
const TARGET_OPTIONS: { label: string; value: string }[] = [
  { label: 'Occupancy', value: 'occupancy' },
  { label: 'ADR (nightly rate)', value: 'adr' },
  { label: 'Mortgage rate %', value: 'mortgage_rate_pct' },
  { label: 'Deposit €', value: 'deposit_eur' },
  { label: 'Purchase price €', value: 'purchase_price_eur' },
  { label: 'Sale price €', value: 'sale_price_eur' },
];
const OP_OPTIONS: { label: string; value: 'mul' | 'add' | 'set' }[] = [
  { label: '× multiply', value: 'mul' },
  { label: '+ add', value: 'add' },
  { label: '= set', value: 'set' },
];
const OP_SYM: Record<string, string> = { mul: '×', add: '+', set: '=' };
const targetLabel = (v: string) => TARGET_OPTIONS.find((t) => t.value === v)?.label ?? v;
const describeOverrides = (ovs: Override[]) =>
  ovs.map((o) => `${targetLabel(o.target)} ${OP_SYM[o.op]}${o.value}`).join(', ');

const fmtEur = (v: number) => '€' + Math.round(v || 0).toLocaleString();
const fmtSigned = (v: number) => (v >= 0 ? '+' : '−') + '€' + Math.abs(Math.round(v)).toLocaleString();

const DeltaBars: React.FC<{ scenarios: any[] }> = ({ scenarios }) => {
  const rows = scenarios.filter((s) => s.name !== 'Base');
  if (!rows.length) return null;
  const W = 720, H = 60 + rows.length * 34, padL = 150, padR = 70;
  const plotW = W - padL - padR;
  const maxAbs = Math.max(1, ...rows.map((s) => Math.abs(s.delta_vs_base.final_after_tax_total)));
  const zeroX = padL + plotW / 2;
  const scale = (plotW / 2) / maxAbs;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      <line x1={zeroX} y1={20} x2={zeroX} y2={H - 20} stroke="#9ca3af" strokeWidth={1} />
      <text x={zeroX} y={14} fontSize="10" fill="#6b7280" textAnchor="middle">Base</text>
      {rows.map((s, i) => {
        const d = s.delta_vs_base.final_after_tax_total;
        const y = 30 + i * 34;
        const w = Math.abs(d) * scale;
        const x = d >= 0 ? zeroX : zeroX - w;
        return (
          <g key={s.name}>
            <text x={padL - 8} y={y + 15} fontSize="12" fill="#374151" textAnchor="end">{s.name}</text>
            <rect x={x} y={y} width={Math.max(w, 1)} height={20} fill={d >= 0 ? '#16a34a' : '#dc2626'} opacity={0.85} />
            <text x={d >= 0 ? x + w + 4 : x - 4} y={y + 15} fontSize="11" fill="#374151"
              textAnchor={d >= 0 ? 'start' : 'end'}>{fmtSigned(d)}</text>
          </g>
        );
      })}
    </svg>
  );
};

const LINE_PALETTE = ['#dc2626', '#16a34a', '#2563eb', '#d97706', '#7c3aed', '#db2777', '#0891b2'];

// Overlay each scenario's cumulative (after-tax) trajectory on one chart so the
// spread between scenarios is visible over time, not just as end-point totals.
export const ScenarioLines: React.FC<{ scenarios: any[] }> = ({ scenarios }) => {
  const series = scenarios.filter((s) => Array.isArray(s.series) && s.series.length);
  if (!series.length) return null;
  const labels: string[] = series[0].series.map((p: any) => p.label);
  const n = labels.length;
  const vals = series.flatMap((s) => s.series.map((p: any) => p.cumulative as number));
  let lo = Math.min(0, ...vals), hi = Math.max(0, ...vals);
  if (lo === hi) hi = lo + 1;
  const W = 760, H = 320, padL = 64, padR = 130, padT = 16, padB = 28;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const x = (i: number) => padL + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const y = (v: number) => padT + plotH - ((v - lo) / (hi - lo)) * plotH;
  const colorFor = (s: any, i: number) => (s.name === 'Base' ? '#0f766e' : LINE_PALETTE[i % LINE_PALETTE.length]);
  const fmtK = (v: number) => '€' + Math.round(v / 1000).toLocaleString() + 'k';
  const zeroY = y(0);
  const showZero = lo < 0 && hi > 0;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      {showZero && <line x1={padL} y1={zeroY} x2={padL + plotW} y2={zeroY} stroke="#9ca3af" strokeDasharray="4 3" strokeWidth={1} />}
      <text x={padL - 8} y={y(hi) + 4} fontSize="10" fill="#6b7280" textAnchor="end">{fmtK(hi)}</text>
      <text x={padL - 8} y={y(lo) + 4} fontSize="10" fill="#6b7280" textAnchor="end">{fmtK(lo)}</text>
      {showZero && <text x={padL - 8} y={zeroY + 4} fontSize="10" fill="#6b7280" textAnchor="end">€0</text>}
      <text x={padL} y={H - 8} fontSize="10" fill="#6b7280" textAnchor="start">{labels[0]}</text>
      <text x={padL + plotW} y={H - 8} fontSize="10" fill="#6b7280" textAnchor="end">{labels[n - 1]}</text>
      {series.map((s, i) => (
        <polyline key={s.name} fill="none" stroke={colorFor(s, i)} strokeWidth={s.name === 'Base' ? 2.5 : 1.75}
          opacity={0.9} points={s.series.map((p: any, j: number) => `${x(j)},${y(p.cumulative)}`).join(' ')} />
      ))}
      {series.map((s, i) => (
        <g key={s.name + '-leg'} transform={`translate(${padL + plotW + 12}, ${padT + 6 + i * 18})`}>
          <rect width={10} height={10} rx={2} fill={colorFor(s, i)} />
          <text x={16} y={9} fontSize="11" fill="#374151">{s.name}</text>
        </g>
      ))}
    </svg>
  );
};

const ScenarioComparison: React.FC<Props> = ({ properties, startDate, endDate, granularity, growthRates }) => {
  const [presets, setPresets] = useState<Preset[]>(DEFAULT_PRESETS);
  const [results, setResults] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customScenarios, setCustomScenarios] = useState<Preset[]>([]);
  const [customName, setCustomName] = useState('');
  const [customRows, setCustomRows] = useState<Override[]>([{ target: 'occupancy', op: 'mul', value: 1 }]);

  const toggle = (i: number) =>
    setPresets((p) => p.map((s, j) => (j === i ? { ...s, enabled: !s.enabled } : s)));
  const toggleCustom = (i: number) =>
    setCustomScenarios((cs) => cs.map((s, j) => (j === i ? { ...s, enabled: !s.enabled } : s)));
  const setRow = (i: number, patch: Partial<Override>) =>
    setCustomRows((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addRow = () => setCustomRows((rows) => [...rows, { target: 'adr', op: 'mul', value: 1 }]);
  const removeRow = (i: number) => setCustomRows((rows) => rows.filter((_, j) => j !== i));
  const addCustomScenario = () => {
    const overrides = customRows.filter((r) => Number.isFinite(r.value));
    if (!overrides.length) { setError('Add at least one override with a number to build a custom scenario.'); return; }
    const name = customName.trim() || `Custom ${customScenarios.length + 1}`;
    setError(null);
    setCustomScenarios((cs) => [...cs, { name, description: describeOverrides(overrides), overrides, enabled: true }]);
    setCustomName('');
    setCustomRows([{ target: 'occupancy', op: 'mul', value: 1 }]);
  };

  const run = async () => {
    if (properties.length === 0) { setError('Select at least one property in the analysis above.'); return; }
    const growth = parseGrowthRates(growthRates);
    if (!growth.ok) { setError(growth.error); return; }
    const undated = missingPricesAsOf(properties);
    if (undated.length > 0) { setError(`Set "Prices As Of" on: ${undated.join(', ')}`); return; }
    const chosen = [...presets.filter((p) => p.enabled), ...customScenarios.filter((p) => p.enabled)];
    setLoading(true); setError(null); setResults(null);
    try {
      const base = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const resp = await fetch(`${base}/api/v1/portfolio/scenarios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          properties, start_date: startDate, end_date: endDate, granularity,
          ...growth.rates,
          scenarios: chosen.map(({ name, overrides }) => ({ name, overrides })),
        }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => null);
        throw new Error(body?.detail || `HTTP ${resp.status}`);
      }
      setResults((await resp.json()).scenarios);
    } catch (e: any) {
      setError(e?.message || 'Scenario comparison failed');
    } finally {
      setLoading(false);
    }
  };

  const baseTotal = results?.find((s) => s.name === 'Base')?.final_after_tax_total ?? 0;

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
      <h3 className="text-lg font-semibold text-gray-800 mb-1">Scenario Comparison</h3>
      <p className="text-sm text-gray-500 mb-4">
        Compare what-if assumptions against the base case. Each scenario re-runs the full engine.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
        {presets.map((p, i) => (
          <label key={p.name} className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={p.enabled} onChange={() => toggle(i)}
              className="h-4 w-4 text-vista-teal-600 focus:ring-vista-teal-500 border-gray-300 rounded" />
            <span className="font-medium">{p.name}</span>
            <span className="text-gray-400 text-xs">({p.description})</span>
          </label>
        ))}
      </div>

      {customScenarios.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
          {customScenarios.map((p, i) => (
            <label key={`custom-${i}`} className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={p.enabled} onChange={() => toggleCustom(i)}
                className="h-4 w-4 text-vista-teal-600 focus:ring-vista-teal-500 border-gray-300 rounded" />
              <span className="font-medium">{p.name}</span>
              <span className="text-gray-400 text-xs">({p.description})</span>
              <button type="button" onClick={() => setCustomScenarios((cs) => cs.filter((_, j) => j !== i))}
                className="ml-auto text-gray-400 hover:text-red-600 text-xs">remove</button>
            </label>
          ))}
        </div>
      )}

      <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Build your own scenario</h4>
        <input value={customName} onChange={(e) => setCustomName(e.target.value)} placeholder="Scenario name (optional)"
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm w-56 mb-3" />
        <div className="space-y-2">
          {customRows.map((r, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <select value={r.target} onChange={(e) => setRow(i, { target: e.target.value })}
                className="px-2 py-1.5 border border-gray-300 rounded-md text-sm">
                {TARGET_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <select value={r.op} onChange={(e) => setRow(i, { op: e.target.value as Override['op'] })}
                className="px-2 py-1.5 border border-gray-300 rounded-md text-sm">
                {OP_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <input type="number" step="any" value={Number.isFinite(r.value) ? r.value : ''}
                onChange={(e) => setRow(i, { value: parseFloat(e.target.value) })}
                className="px-2 py-1.5 border border-gray-300 rounded-md text-sm w-28" />
              {customRows.length > 1 && (
                <button type="button" onClick={() => removeRow(i)}
                  className="text-gray-400 hover:text-red-600 text-sm px-1" aria-label="Remove override">✕</button>
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-3">
          <button type="button" onClick={addRow}
            className="text-vista-teal-600 hover:text-vista-teal-700 text-sm font-medium">+ Add override</button>
          <button type="button" onClick={addCustomScenario}
            className="bg-gray-700 text-white px-4 py-1.5 rounded-md hover:bg-gray-800 transition-colors text-sm">Add scenario</button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          × scales (Occupancy ×1.2), + adds (Mortgage rate +0.5), = sets an absolute value. Added scenarios appear above as toggles.
        </p>
      </div>

      <button onClick={run} disabled={loading || properties.length === 0}
        className="bg-vista-teal-600 text-white px-5 py-2 rounded-md hover:bg-vista-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
        {loading ? 'Running scenarios…' : 'Run scenario comparison'}
      </button>
      {error && <span className="ml-4 text-red-600 text-sm">{error}</span>}

      {results && (
        <div className="mt-6">
          <TaxAssumptionNote className="mb-4" />
          <div className="overflow-x-auto border border-gray-200 rounded-lg mb-6">
            <table className="min-w-full text-sm divide-y divide-gray-200">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Scenario</th>
                  <th className="px-4 py-2 text-right font-medium">Final (after tax)</th>
                  <th className="px-4 py-2 text-right font-medium">Δ vs base</th>
                  <th className="px-4 py-2 text-right font-medium">Δ %</th>
                  <th className="px-4 py-2 text-right font-medium">Net cashflow</th>
                  <th className="px-4 py-2 text-right font-medium" title="Money-weighted return on the dated after-tax flows (needs a sale in the window)">IRR after tax</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {results.map((s) => {
                  const d = s.delta_vs_base.final_after_tax_total;
                  const pct = baseTotal !== 0 ? (d / Math.abs(baseTotal)) * 100 : 0;
                  const isBase = s.name === 'Base';
                  return (
                    <tr key={s.name} className={isBase ? 'bg-vista-teal-50 font-medium' : ''}>
                      <td className="px-4 py-2 text-gray-800">{s.name}</td>
                      <td className="px-4 py-2 text-right">{fmtEur(s.final_after_tax_total)}</td>
                      <td className={`px-4 py-2 text-right ${isBase ? 'text-gray-400' : d >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {isBase ? '—' : fmtSigned(d)}
                      </td>
                      <td className={`px-4 py-2 text-right ${isBase ? 'text-gray-400' : d >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {isBase ? '—' : `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-600">{fmtEur(s.total_net_cashflow)}</td>
                      <td className="px-4 py-2 text-right text-gray-600" title={s.irr_note || ''}>
                        {s.irr_after_tax_pct != null ? `${s.irr_after_tax_pct.toFixed(2)}%` : 'n/a'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Difference from base (final after-tax total)</h4>
          <DeltaBars scenarios={results} />
          <h4 className="text-sm font-semibold text-gray-700 mb-2 mt-6">Cumulative trajectory by scenario</h4>
          <ScenarioLines scenarios={results} />
        </div>
      )}
    </div>
  );
};

export default ScenarioComparison;
